import React, { useMemo } from 'react';
import { marked } from 'marked';
import './reportView.css';
import { parseReportMarkdown, ReportContentBlock, ReportSection, ReportTable } from './parseMarkdown';
import type { ReportSectionsResponse } from '../services/api';

interface InteractiveReportProps {
  markdown: string;
  titleOverride?: string;
  onNavigateToReport?: (reportId: string) => void;
  reportSections?: ReportSectionsResponse | null;
  isFinalReport?: boolean;
  hideMicroreportLinks?: boolean;
}

const escapeLoneTildes = (text: string): string =>
  text.replace(/(?<!~)~(?!~)/g, '\\~');

const linkRenderer = new marked.Renderer();
linkRenderer.link = (href, title, text) => {
  const safeHref = href ?? '';
  const titleAttr = title ? ` title="${title}"` : '';
  return `<a href="${safeHref}" target="_blank" rel="noopener noreferrer"${titleAttr}>${text}</a>`;
};

const markdownToHtml = (text: string): string => {
  if (!text) return '';
  try {
    const sanitized = escapeLoneTildes(text);
    return marked.parse(sanitized, { gfm: true, breaks: true, renderer: linkRenderer }) as string;
  } catch (err) {
    console.error('[InteractiveReport] markdown parse error:', err);
    return `<p>${text.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>`;
  }
};

const renderCellHtml = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }
  const text = typeof value === 'string' ? value : String(value);
  if (!text.trim()) {
    return '';
  }
  return markdownToHtml(text.trim());
};

const deriveColumnsFromRows = (rows: ReportTable['rows']): string[] => {
  if (!rows || rows.length === 0) {
    return ['Column 1', 'Column 2'];
  }
  const keys: string[] = [];
  const seen = new Set<string>();
  rows.forEach((row) => {
    if (row && typeof row === 'object') {
      Object.keys(row).forEach((key) => {
        if (!seen.has(key)) {
          seen.add(key);
          keys.push(key);
        }
      });
    }
  });
  return keys.length ? keys : ['Value'];
};

const renderTable = (table: ReportTable) => {
  const columns =
    (Array.isArray(table.columns) && table.columns.length > 0
      ? table.columns
      : deriveColumnsFromRows(table.rows)) ?? [];

  if (table.parseFailed && (!table.rows || table.rows.length === 0)) {
    return (
      <div className="table-block table-block--fallback" key={table.name}>
        {table.name && <h3 className="table-title">{table.name}</h3>}
        <p className="table-fallback-notice">
          This table could not be rendered automatically. Raw data shown below.
        </p>
        <pre className="table-fallback-raw">{table.rawBody}</pre>
      </div>
    );
  }

  return (
    <div className="table-block" key={table.name}>
      {table.name && <h3 className="table-title">{table.name}</h3>}
      <div className="table-wrapper">
        <table className="report-table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column}>{column}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(table.rows || []).map((row, idx) => (
              <tr key={`${table.name}-${idx}`}>
                {columns.map((column) => {
                  const rawValue = row && typeof row === 'object' ? row[column] : '';
                  const html = renderCellHtml(rawValue);
                  return (
                    <td
                      key={`${column}-${idx}`}
                      dangerouslySetInnerHTML={{ __html: html }}
                    />
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const renderBlock = (block: ReportContentBlock, idx: number) => {
  if (block.type === 'table') {
    return <div key={`table-${idx}`}>{renderTable(block.table)}</div>;
  }
  return (
    <div
      key={`text-${idx}`}
      className="section-summary"
      dangerouslySetInnerHTML={{ __html: markdownToHtml(block.content) }}
    />
  );
};

type FinalReportSectionEntry = {
  expectedTitle?: string;
  microreportId?: string;
};

const normalizeSectionLabel = (value: string): string =>
  (value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const buildFinalReportSectionEntries = (
  reportSections?: ReportSectionsResponse | null
): FinalReportSectionEntry[] => {
  if (!reportSections || !reportSections.final_report) {
    return [];
  }

  const microreportNameToId = new Map<string, string>();
  (reportSections.microreports || []).forEach((microreport, idx) => {
    const microreportId = String(microreport.id || `microreport${idx + 1}`).trim();
    const microreportName = normalizeSectionLabel(microreport.name || '');
    if (microreportId && microreportName) {
      microreportNameToId.set(microreportName, microreportId);
    }
  });

  return Object.entries(reportSections.final_report)
    .sort(([left], [right]) => {
      const leftMatch = left.match(/(\d+)/);
      const rightMatch = right.match(/(\d+)/);
      const leftOrder = leftMatch ? parseInt(leftMatch[1], 10) : Number.MAX_SAFE_INTEGER;
      const rightOrder = rightMatch ? parseInt(rightMatch[1], 10) : Number.MAX_SAFE_INTEGER;
      return leftOrder - rightOrder;
    })
    .map(([, value]) => {
      if (typeof value === 'string') {
        const microreportMatch = value.match(/^microreport\s*-\s*(.+)$/i);
        if (microreportMatch) {
          const expectedTitle = microreportMatch[1].trim();
          return {
            expectedTitle,
            microreportId: microreportNameToId.get(normalizeSectionLabel(expectedTitle)),
          };
        }
        return { expectedTitle: value.trim() };
      }
      if (value && typeof value === 'object') {
        const raw = value as Record<string, unknown>;
        const expectedTitle =
          typeof raw.title === 'string' && raw.title.trim().length > 0
            ? raw.title.trim()
            : undefined;
        const microreportId =
          typeof raw.microreport_id === 'string' && raw.microreport_id.trim().length > 0
            ? raw.microreport_id.trim()
            : typeof raw.id === 'string' && raw.id.trim().length > 0
              ? raw.id.trim()
              : undefined;
        return { expectedTitle, microreportId };
      }
      return {};
    });
};

const attachMicroreportIds = (
  sections: ReportSection[],
  reportSections?: ReportSectionsResponse | null
): ReportSection[] => {
  const entries = buildFinalReportSectionEntries(reportSections);
  if (!entries.length) {
    return sections;
  }

  const eligibleIndices = sections
    .map((section, idx) => ({ section, idx }))
    .filter(({ section }) => {
      const normalizedHeading = normalizeSectionLabel(section.heading);
      return section.id !== 'summary' && normalizedHeading !== 'sources' && normalizedHeading !== 'citations';
    })
    .map(({ idx }) => idx);

  if (!eligibleIndices.length) {
    return sections;
  }

  const nextSections = sections.map((section) => ({ ...section }));
  const consumedEntries = new Set<number>();

  eligibleIndices.forEach((sectionIndex) => {
    if (nextSections[sectionIndex].microreportId) {
      return;
    }
    const normalizedHeading = normalizeSectionLabel(nextSections[sectionIndex].heading);
    if (!normalizedHeading) {
      return;
    }
    const matchedEntryIndex = entries.findIndex((entry, entryIndex) => {
      if (consumedEntries.has(entryIndex) || !entry.microreportId || !entry.expectedTitle) {
        return false;
      }
      return normalizeSectionLabel(entry.expectedTitle) === normalizedHeading;
    });
    if (matchedEntryIndex === -1) {
      return;
    }
    nextSections[sectionIndex].microreportId = entries[matchedEntryIndex].microreportId;
    consumedEntries.add(matchedEntryIndex);
  });

  let entryCursor = 0;
  eligibleIndices.forEach((sectionIndex) => {
    while (entryCursor < entries.length && consumedEntries.has(entryCursor)) {
      entryCursor += 1;
    }
    if (entryCursor >= entries.length) {
      return;
    }
    if (!nextSections[sectionIndex].microreportId && entries[entryCursor].microreportId) {
      nextSections[sectionIndex].microreportId = entries[entryCursor].microreportId;
    }
    consumedEntries.add(entryCursor);
    entryCursor += 1;
  });

  return nextSections;
};

const renderSection = (
  section: ReportSection,
  onNavigateToReport?: (reportId: string) => void,
  isFinalReport?: boolean,
  hideMicroreportLinks?: boolean
) => {
  const hasTables = section.blocks.some((block) => block.type === 'table');
  const sectionClasses = ['report-section'];
  if (hasTables) {
    sectionClasses.push('report-section-data');
  }

  const canNavigate = isFinalReport && !hideMicroreportLinks && section.microreportId && onNavigateToReport;

  return (
    <section className={sectionClasses.join(' ')} key={section.id}>
      <div className="section-header">
        <div className="section-heading-row">
          <h2 className="section-heading">{section.heading}</h2>
        </div>
        {canNavigate && (
          <button
            type="button"
            className="section-microreport-link"
            onClick={() => onNavigateToReport?.(section.microreportId!)}
          >
            click here to view detailed microreport
          </button>
        )}
      </div>
      <div className="section-body">{section.blocks.map((block, idx) => renderBlock(block, idx))}</div>
    </section>
  );
};

const InteractiveReport: React.FC<InteractiveReportProps> = ({
  markdown,
  titleOverride,
  onNavigateToReport,
  reportSections = null,
  isFinalReport = false,
  hideMicroreportLinks = false,
}) => {
  const parsed = useMemo(() => {
    try {
      return parseReportMarkdown(markdown || '');
    } catch (err) {
      console.error('[InteractiveReport] parseReportMarkdown error:', err);
      return { title: 'Report', subtitle: '', sections: [] };
    }
  }, [markdown]);
  const linkedSections = useMemo(
    () => attachMicroreportIds(parsed.sections, reportSections),
    [parsed.sections, reportSections]
  );
  const orderedSections = useMemo(() => {
    const sections = [...linkedSections];
    const macroIdx = sections.findIndex((section) =>
      /macro interpretation/i.test(section.heading)
    );
    const signalsIdx = sections.findIndex((section) =>
      /signal/i.test(section.heading)
    );
    if (macroIdx > -1 && signalsIdx > -1 && macroIdx > signalsIdx) {
      const [macroSection] = sections.splice(macroIdx, 1);
      sections.splice(signalsIdx, 0, macroSection);
    }
    return sections;
  }, [linkedSections]);
  const stats = useMemo(() => {
    const tableCount = linkedSections.reduce((total, section) => {
      const tablesInSection = section.blocks.filter((block) => block.type === 'table').length;
      return total + tablesInSection;
    }, 0);
    return {
      sectionCount: linkedSections.length,
      tableCount,
    };
  }, [linkedSections]);

  const reportTitle = titleOverride && titleOverride.trim().length > 0 ? titleOverride : parsed.title;

  return (
    <div className="interactive-report">
      <article className="report-page">
        <header className="report-header">
          <div className="report-title-group">
            <span className="report-eyebrow">Strategic Intelligence</span>
            <h1 className="report-title">{reportTitle}</h1>
            <p className="report-subtitle">{parsed.subtitle}</p>
          </div>
          <div className="report-meta">
            <div className="report-meta-card">
              <span className="meta-label">Sections</span>
              <span className="meta-value">{stats.sectionCount}</span>
            </div>
           </div>
        </header>

        <div className="report-divider" />

        <div className="report-sections">
          {orderedSections.map((section) =>
            renderSection(section, onNavigateToReport, isFinalReport, hideMicroreportLinks)
          )}
        </div>
      </article>
    </div>
  );
};

export default InteractiveReport;

